import type { VideoJsPlayer } from 'video.js';
import type { SourceObject } from 'video.js/dist/types/tech/tech';

import type { HivePluginOptions } from '../player.interface';
import type { HiveErrorMessage, HivePlugin, OptionsHtml5 } from './hive.interface';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import videojs from 'video.js';

import { HiveSeverity } from './hive.interface';
import { useLogger } from '../../../contexts/logger-context';
import { loadScript } from '../../../utils/load-script';
import { ErrorCode, PlayerError } from '../player-errors';
import { SourceType } from '../player.config';

export function useHiveTech(
  enabled: boolean,
  player: VideoJsPlayer,
  setVideoSrc: (videoSrc: SourceObject[], force: boolean) => void,
  options?: HivePluginOptions
): {
  reloadSource: () => void;
} {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'HiveTech' }), [mainLogger]);
  const [sdkLoaded, setSDKLoaded] = useState(false);
  const pluginRef = useRef<HivePlugin | undefined>(undefined);

  // Shared callback for initializing Hive session and loading manifest
  const initializeHiveSession = useCallback(
    async (plugin: HivePlugin, player: VideoJsPlayer, options: HivePluginOptions) => {
      logger.debug('Initializing Hive session.');

      const manifest = await initSession(plugin, player, options);
      logger.info('Hive session initialized and manifest retrieved successfully.');

      setVideoSrc([{ src: manifest, type: SourceType.HLS }], true);
    },
    [logger, setVideoSrc]
  );

  const reloadSource = useCallback(() => {
    if (!enabled) {
      logger.warn('Reloading source skipped, as the Hive SDK is not enabled.');
      return;
    }

    if (!pluginRef.current) {
      logger.warn('Cannot reload source because Hive plugin instance is not available.');
      return;
    }

    if (!options) {
      logger.warn('Cannot reload source because Hive plugin options are not provided.');
      return;
    }

    const init = async (plugin: HivePlugin, player: VideoJsPlayer, options: HivePluginOptions) => {
      try {
        // We initialize (or re-initialize) the Hive session
        // in case of a plugin instance already existing,
        // whenever the options change, as they contain
        // the session initialization parameters (ticket or token).
        await initializeHiveSession(plugin, player, options);
        logger.info('Hive session initialized successfully, reloading source.');
      } catch (error) {
        const errorDetails = error instanceof Error ? error.message : String(error);
        const msg = `The Hive plugin failed to initialize during source reload: ${errorDetails}`;

        logger.error(msg, error);
        pluginRef.current?.closeHiveSession();

        player.error(msg);
      }
    };

    init(pluginRef.current, player, options);
  }, [enabled, player, options, logger, initializeHiveSession]);

  // Load Hive sources and initialize the plugin when enabled
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let alive = true;

    const loadSDK = async () => {
      await Promise.all([
        loadScript(
          'https://media-players.hivestreaming.com/plugins/html5/13.1.0/html5.java.hivejs.hive.min.js',
          'mi-hivejs-sdk'
        ),
        loadScript(
          'https://media-players.hivestreaming.com/common_libs/html5/videojs/hive-module.js',
          'mi-hivejs-videojs-connector'
        ),
      ]);
    };

    if (!window.HiveHtml5 || !window.HiveModule) {
      loadSDK()
        .then(() => {
          if (alive) {
            logger.debug('Hive SDK loaded successfully.');
            setSDKLoaded(true);
          }
        })
        .catch((error) => {
          if (alive) {
            logger.error('Failed to load Hive SDK:', error);
            player.error('Failed to load Hive SDK. Please try again or reload the page.');
          }
        });
    } else {
      if (alive) {
        logger.debug('Hive SDK already loaded, skipping script loading.');
        setSDKLoaded(true);
      }
    }

    return () => {
      alive = false;
    };
  }, [enabled, logger, player]);

  // create Hive plugin and set video source when SDK is loaded
  useEffect(() => {
    if (!enabled || !sdkLoaded || !options) {
      return;
    }

    const createPlugin = (options: OptionsHtml5): HivePlugin => {
      logger.debug('Create Hive plugin.');

      return new window.HiveHtml5(player.tech(false).el(), options);
    };

    const destroyPlugin = () => {
      try {
        pluginRef.current?.destroyHivePlugin();
        pluginRef.current = undefined;
        logger.debug('Hive plugin destroyed successfully.');
      } catch (_) {
        // In the unlikely scenario that the cleanup fails, we do nothing.
        logger.warn('Failed to destroy Hive plugin during cleanup.');
      }
    };

    const closeSession = () => {
      pluginRef.current?.closeHiveSession();
      logger.debug('Hive session closed successfully.');
    };

    const errorHandler = (msg: HiveErrorMessage) => {
      if (msg.severity === HiveSeverity.NORMAL) {
        // No action required
      } else if (msg.severity === HiveSeverity.HIGH) {
        // Session should be re-initialized, ideally with a re-trial mechanism
        closeSession();
        player.error('The Hive plugin has encountered a high severity error.');
      } else if (msg.severity === HiveSeverity.CRITICAL) {
        // Disable the Hive Plugin ( and fallback to the CDN if required)
        destroyPlugin();

        // We trigger a player error to notify the user that the Hive plugin is no longer available.
        // We don't throw an Error here, because it happened while the plugin was running,
        // so we assume that the player is still in a state to handle errors properly.
        player.error('The Hive plugin has encountered a critical error.');
      }
    };

    // In case we have no plugin instance yet, we create one.
    // Otherwise, we assume that the existing instance is still valid and do not re-create it.
    if (!pluginRef.current) {
      const optionsHtml5: OptionsHtml5 = {
        playerName: 'videojs',
        playerVersion: videojs.VERSION,
        eventName: options.eventName,
        debugLevel: options.debugLevel,
        hiveTechOrder: options?.techOrder,
        onError: errorHandler,
      };

      pluginRef.current = createPlugin(optionsHtml5);
      logger.info('Hive plugin created successfully.');
    }

    const init = async (plugin: HivePlugin, player: VideoJsPlayer, options: HivePluginOptions) => {
      try {
        // We initialize the Hive session (or re-initialize it
        // in case of a plugin instance already existing)
        // whenever the options change, as they contain
        // the session initialization parameters (ticket or token).
        await initializeHiveSession(plugin, player, options);
      } catch (error) {
        logger.error('Failed to initialize Hive session:', error);

        destroyPlugin();

        player.error('The Hive plugin failed to initialize.');
      }
    };

    init(pluginRef.current, player, options);

    // Ensure the Hive session is also closed when the underlying player
    // is disposed or encounters an error, not only on React unmount.
    const handlePlayerDispose = () => {
      closeSession();
    };

    const handlePlayerError = () => {
      closeSession();
    };

    player.on('dispose', handlePlayerDispose);
    player.on('error', handlePlayerError);

    return () => {
      // Remove player listeners and close any active Hive session.
      player.off('dispose', handlePlayerDispose);
      player.off('error', handlePlayerError);
      closeSession();
    };
  }, [enabled, sdkLoaded, options, player, logger, initializeHiveSession]);

  return {
    reloadSource,
  };
}

async function initSession(plugin: HivePlugin, player: VideoJsPlayer, options: HivePluginOptions): Promise<string> {
  const sessionOptions = options?.token || options?.ticket;

  if (!sessionOptions) {
    throw new PlayerError(
      ErrorCode.PLAYER_ERR_INIT_HIVE_P2P_FAILED,
      'No ticket or token provided for Hive plugin initialization.'
    );
  }

  const session = await plugin.initSession(sessionOptions);

  // Enable the Hive plugin for the player through the HiveModule connector
  window.HiveModule.enable(plugin, player, videojs);

  return session.manifest;
}
